import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { SIX_PILLARS } from "../data/pillars";
import { ArrowRight, Crown, HeartHandshake, UsersRound, TreePine, Zap, ShieldCheck, Eye, RotateCcw, Shield, CheckCircle, TrendingUp } from "lucide-react";

const PillarsPage = () => {
  const ICONS = [Crown, HeartHandshake, UsersRound, HeartHandshake, TreePine, Eye];
  const pillarsData = SIX_PILLARS.map((p, i) => ({
    id: p.id,
    title: p.name,
    description: p.summary,
    icon: ICONS[i] || Shield,
    details: p.keyPractices
  }));

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-24">
        {/* Hero Banner */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-16">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1620519086896-1cfd9f8114ab?auto=format&fit=crop&w=1920"
              alt="6 Pillars Framework Foundation"
              className="w-full h-full object-cover"
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/70 via-teal-900/65 to-cyan-900/70"></div>
          </div>
          
          {/* Content */}
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
                The 6 Pillars of{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">
                  Conscious Enterprise
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-12 leading-relaxed">
                Explore the framework we assess—leadership, culture, products, customers, community and brand collaboration. Six essential dimensions of conscious business.
              </p>
              <a href="/signup">
                <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-10 py-6">
                  Get Certified
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Framework Image - Circular Chart */}
        <section className="py-16 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="rounded-2xl overflow-hidden shadow-2xl bg-white p-8">
                <img 
                  src="/six_pillars_chart.png"
                  alt="CNESS 6 Pillars Framework - Compassionate Excellence"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Detailed Pillars */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-900 mb-16 text-center">
                Framework Deep Dive
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {pillarsData.map((pillar) => (
                  <Card key={pillar.id} className="group hover:shadow-2xl hover:-translate-y-2 transition-all duration-300">
                    <CardContent className="p-8">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center group-hover:from-amber-500 group-hover:to-amber-600 transition-all duration-300 flex-shrink-0">
                          <pillar.icon className="h-6 w-6 text-amber-600 group-hover:text-white transition-colors duration-300" />
                        </div>
                        
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-amber-600 transition-colors duration-300">
                            {pillar.title}
                          </h3>
                          <p className="text-gray-600 mb-4 leading-relaxed">
                            {pillar.description}
                          </p>
                          
                          <div className="space-y-2">
                            <h4 className="font-semibold text-gray-900 text-sm">Key Assessment Areas:</h4>
                            {pillar.details.map((detail, index) => (
                              <div key={index} className="flex items-center text-sm text-gray-600">
                                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2 flex-shrink-0" />
                                {detail}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Key Elements Section */}
        <section className="py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-white mb-6">
                  Key Elements of CNESS Works
                </h2>
                <p className="text-xl text-blue-200 max-w-3xl mx-auto">
                  Discover what makes CNESS Works the leading platform for conscious business validation
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Brand Trust and Credibility */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center mb-6">
                    <Shield className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Brand Trust & Credibility</h3>
                  <p className="text-blue-100 leading-relaxed">
                    CNESS Works certification allows organisations to publicly validate their values and conscious commitments, building trust with stakeholders and customers who demand authenticity.
                  </p>
                </div>

                {/* Evidence and Best Practices */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center mb-6">
                    <CheckCircle className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Evidence & Best Practices</h3>
                  <p className="text-blue-100 leading-relaxed">
                    Companies can share and adopt best practices through the hub, reinforcing continuous improvement and transparency across the conscious business community.
                  </p>
                </div>

                {/* Measurable Impact */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center mb-6">
                    <TrendingUp className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Measurable Impact</h3>
                  <p className="text-blue-100 leading-relaxed">
                    The platform connects conscious actions to quantifiable social and environmental outcomes, enabling organisations to demonstrate real-world impact with data-driven evidence.
                  </p>
                </div>

                {/* Live and Operational */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mb-6">
                    <Zap className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Live & Operational</h3>
                  <p className="text-blue-100 leading-relaxed">
                    CNESS Works is not theoretical—it is an active platform where real organisations are engaging, learning and growing every day toward conscious excellence.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Assessment Approach */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold text-gray-900 mb-8">
                How We Assess Each Pillar
              </h2>
              <p className="text-xl text-gray-600 mb-12">
                Our comprehensive assessment methodology ensures thorough evaluation across all dimensions of conscious business practice.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    title: "Evidence-Based",
                    description: "Documentation, policies, and real-world implementation examples"
                  },
                  {
                    title: "Stakeholder Input", 
                    description: "Feedback from employees, customers, partners, and community members"
                  },
                  {
                    title: "Performance Metrics",
                    description: "Quantitative measures and outcomes demonstrating impact and progress"
                  }
                ].map((approach, index) => (
                  <div key={index} className="p-6 bg-gray-50 rounded-lg">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{approach.title}</h3>
                    <p className="text-gray-600">{approach.description}</p>
                  </div>
                ))}
              </div>
              
              <div className="mt-12">
                <a href="/signup">
                  <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white">
                    Begin Your Assessment
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PillarsPage;